import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Shared breakpoint between the desktop two-pane voice layout and the mobile
 * single-pane voice layout. 760px is the standard portrait-tablet boundary;
 * iPads in portrait still get the desktop layout, narrow phones get mobile.
 *
 * Use the exported media query string (`VOICE_COMPACT_MEDIA_QUERY`) for CSS
 * `@media` rules to keep the breakpoint in one place.
 */
export const VOICE_COMPACT_MEDIA_QUERY = '(max-width: 759.98px)';

const SSR_DEFAULT = false;

/**
 * Allows a parent to force the compact layout independently of `window.matchMedia`.
 * Used by the shopperos-admin "Tools & Widgets" preview page so the mobile
 * preview frame renders the actual mobile layout regardless of the browser
 * window size. Real consumer surfaces never provide this context — the hook
 * falls through to `matchMedia` as before.
 */
const CompactLayoutOverrideContext = createContext<boolean | null>(null);

export const CompactLayoutOverrideProvider: React.FC<{ value: boolean | null; children: React.ReactNode }> = ({
  value,
  children,
}) => {
  return React.createElement(CompactLayoutOverrideContext.Provider, { value }, children);
};

/**
 * Subscribes to a `matchMedia` listener for the voice-mode compact breakpoint.
 * Returns `true` for narrow viewports (<760px) — i.e. when the voice call
 * surface should collapse to a single-pane mobile layout.
 *
 * Safe to call during SSR: returns `false` until the first effect runs in
 * the browser, then re-renders if the media query matches.
 *
 * If a `CompactLayoutOverrideProvider` is mounted higher in the tree with a
 * non-null value, that value wins. Used by preview/test surfaces.
 */
export function useIsCompactVoiceLayout(): boolean {
  const override = useContext(CompactLayoutOverrideContext);
  const [isCompact, setIsCompact] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return SSR_DEFAULT;
    }
    return window.matchMedia(VOICE_COMPACT_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(VOICE_COMPACT_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    setIsCompact(mql.matches);
    // Modern browsers expose `addEventListener('change', …)`; Safari < 14 only has `addListener`.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mql as any).addListener?.(handler);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mql as any).removeListener?.(handler);
    };
  }, []);

  if (override !== null) return override;
  return isCompact;
}

export default useIsCompactVoiceLayout;
